import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Image,
  Linking,
  StyleSheet,
  Modal,
  Pressable,
  ScrollView,
  Platform,
  ActivityIndicator,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import backButton from './assets/back1.png';
import notification from './assets/notification1.png';
import KidInfoModal from './KidInfoModal';
import axios from 'axios';
import API_PATHS from '../src/apiCalls/ApiPath';
import { useFocusEffect } from '@react-navigation/native';
import Toast from "react-native-toast-message";
import TopGreenHeader from './screens/Common/TopGreenHeader';
import { isEventServiceType, eventTypeLabel } from './screens/Common/helper';
// import { toast, ToastContainer } from 'react-toastify';
// import { ReloadInstructions } from 'react-native/Libraries/NewAppScreen';
//import { ScrollView } from 'react-native-web';

export default function BookingDetails({ navigation, route }) {
  const bookingId = route.params.bookingId || route.params.booking.bookingId;

  const [bookingData, setBookingData] = useState(null);
  const [bookingStatus, setBookingStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [kidData, setKidData] = useState(null);
  const [timer, setTimer] = useState('');
  const [rerender, setRerender] = useState(false);

  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [endJobModalVisible, setEndJobModalVisible] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [loggedInUserId, setLoggedInUserId] = useState(null);
  const CANCELLATION_FEE = 10;

  const typeDescriptionMap = {
    HOMECARE: 'At Home Care',
    LEARNCARE: 'At Learning Center',
    TRANSPORT: 'Transportation',
    BIRTHDAY: 'Birthday Party',
    VENREN: 'Venue Rental',
    BREAKFASTCLUB: 'Breakfast Club',
    SOCIALHUB: 'Social Hub',
    'EVENT-STARTER': 'Starter DIY Package',
    'EVENT-DELUX': 'Deluxe Custom Packages',
    'EVENT-SIGNATURE': 'Signature Curated Packages',
    'EVENT-PARTY_HELPER': 'Party Helpers',
  };

  // Event bookings show a single start time (like the old Venue Rental flow).
  // Matches every event code format the backend sends (EVENT-*, EVENTS_*, ...).
  const isEventType = isEventServiceType;

  const handleBack = () => {
    navigation.navigate('Bookings');
  };

  const handleApprove = async () => {
    setLoading(true);
    const token = await AsyncStorage.getItem('token');
    if (!token) {
      return;
    }
    try {
      const response = await axios.post(
        `${API_PATHS.BASE_URL}${API_PATHS.EDIT_BOOKING}`,
        {
          bookingId: bookingId,
          status: 'APPROVED',
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.Success === 1) {
        Toast.show({
          type: "success",
          text1: "Booking approved successfully",
        });
        setRerender(!rerender);
      } else {
        Toast.show({
          type: "error",
          text1: "An error occurred while updating the booking.",
        });
      }
    } catch (error) {
      Toast.show({
        type: "error",
        text1: "An error occurred while updating the booking.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!cancelReason.trim()) {
      Toast.show({
        type: "error",
        text1: "Please enter a reason for cancellation",
      });
      return;
    }

    setLoading(true);
    const token = await AsyncStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${API_PATHS.BASE_URL}${API_PATHS.CANCEL_BOOKING}`,
        {
          bookingId: bookingId,
          comment: cancelReason,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.Success === 1) {
        Toast.show({
          type: "success",
          text1: "Booking cancelled successfully",
        });
        setCancelModalVisible(false);
        setCancelReason('');
        setRerender(!rerender);
      } else {
        Toast.show({
          type: "error",
          text1: response.data.Message || "Failed to cancel the booking. Please contact customer care",
        });
      }
    } catch (error) {
      Toast.show({
        type: "error",
        text1: "Failed to cancel the booking. Please contact customer care",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEndJob = async () => {
    setLoading(true);
    const token = await AsyncStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const currentEpochSeconds = Math.floor(Date.now() / 1000);
      const response = await axios.post(
        `${API_PATHS.BASE_URL}${API_PATHS.EDIT_BOOKING}`,
        {
          bookingId: bookingId,
          serviceEndTime: currentEpochSeconds,
          status: 'ENDED',
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.Success === 1) {
        Toast.show({
          type: "success",
          text1: "Job ended successfully",
        });
        setEndJobModalVisible(false);
        setRerender(!rerender);
      } else {
        Toast.show({
          type: "error",
          text1: response.data.Message || "Failed to end the job. Please try again.",
        });
      }
    } catch (error) {
      Toast.show({
        type: "error",
        text1: "Failed to end the job. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const isWithin24Hrs = () => {
    if (!bookingData?.startTime) return false;
    const startTimeMs = bookingData.startTime * 1000;
    const currentTimeMs = Date.now();
    const diffMs = startTimeMs - currentTimeMs;
    const twentyFourHrsMs = 24 * 60 * 60 * 1000;
    return diffMs > 0 && diffMs <= twentyFourHrsMs;
  };

  const isWithinChatWindow = () => {
    if (bookingData?.status === 'ONGOING') return true;
    if (!bookingData?.startTime) return false;
    const twentyFourHrsMs = 24 * 60 * 60 * 1000;
    return Date.now() >= bookingData.startTime * 1000 - twentyFourHrsMs;
  };

  const canShowChatButton = () =>
    !!loggedInUserId &&
    String(loggedInUserId) === String(bookingData?.parent?.parentId) &&
    (bookingData?.status === 'ONGOING' ||
      (bookingData?.status === 'ACCEPTED' && isWithinChatWindow()));

  const handleOpenChat = async () => {
    const parentId = bookingData?.parent?.parentId;
    const parentName = bookingData?.parent?.name;
    const staffId = bookingData?.staff?.staffId;
    const staffName = bookingData?.staff?.name;

    if (!parentId || !staffId || !bookingId) {
      Toast.show({
        type: "error",
        text1: "Unable to open chat",
      });
      return;
    }

    const token = await AsyncStorage.getItem('token');
    const presentTime = Math.floor(Date.now() / 1000);

    let isChatAllowed = true;
    try {
      const response = await axios.post(
        `${API_PATHS.BASE_URL}${API_PATHS.IS_CHAT_ALLOWED}`,
        {
          bookingId,
          presentTime,
          senderId: parentId,
          receiverId: staffId,
          token,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (response.data?.Data?.isChatAllowed === false) {
        isChatAllowed = false;
      }
    } catch (err) {
      // Fail open, same as the staff app's chat gate.
    }

    const queryParams = {
      type: 'client',
      staffId,
      staffName,
      clientId: parentId,
      clientName: parentName,
      bookingId,
      token,
      ...(isChatAllowed ? {} : { toClose: 'true' }),
    };

    const query = Object.entries(queryParams)
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');

    const url = `${API_PATHS.CHAT_URL}?${query}`;

    if (Platform.OS === 'web') {
      window.open(url, '_blank');
    } else {
      navigation.navigate('ChatWebView', { url });
    }
  };

  const timeConverter = epochTime => {
    const date = new Date(epochTime * 1000); // Multiply by 1000 to convert to milliseconds

    const day = date.getDate().toString().padStart(2, '0'); // Ensure two-digit day
    const fullMonthName = date.toLocaleString('default', { month: 'long' });
    const month = fullMonthName.length > 4 ? fullMonthName.slice(0, 3) : fullMonthName; // Get abbreviated month
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const getTime = () => {
    const From = timeConverter(bookingData.startTime);
    return From;
  };

  function formatEpochToLocalTime(epochTime) {
    const date = new Date(epochTime * 1000);
    const day = date.getDate().toString().padStart(2, '0'); // Ensure two-digit day
    const month = date.toLocaleString('default', { month: 'short' }).toUpperCase(); // Abbreviated month
    const year = date.getFullYear().toString().slice(-2); // Last two digits of year

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0'); // Two-digit minutes
    const period = hours < 12 ? 'AM' : 'PM';

    const formattedHours = hours % 12 || 12;
    const formattedMinutes = minutes.toString().padStart(2, '0'); // Two-digit minutes

    return `${day}-${month}-${year}, ${formattedHours}:${formattedMinutes} ${period}`;
  }

  function EpochTime(epochTime) {
    const date = new Date(epochTime * 1000); // Multiply by 1000 to convert to milliseconds

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0'); // Two-digit minutes
    const period = hours < 12 ? 'AM' : 'PM';

    const formattedHours = hours % 12 || 12; // Convert 24-hour to 12-hour format

    return `${formattedHours}:${minutes} ${period}`;
  }

  const getBookingDetail = useCallback(async () => {
    const token = await AsyncStorage.getItem('token');
    if (!token) {
      return;
    }
    try {
      await axios
        .get(`${API_PATHS.BASE_URL}${API_PATHS.GET_BOOKING_DETAILS}/${bookingId}`, {
          headers: {
            'Content-Type': 'application/json',
            authorization: `Bearer ${token}`,
          },
        })
        .then(response => {
          setBookingData(response.data.Data);
          if (bookingStatus != response.data.Data.status) {
            setBookingStatus(response.data.Data.status);
          }
        });
    } catch (err) {
      //console.log(err.response.data);
    }
  }, [bookingId, bookingStatus]);

  function calculateServiceTime(startTime, endTime) {
    const timeDifference = endTime - startTime;
    const totalMinutes = Math.ceil(timeDifference / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${hours}hrs ${minutes}mins`;
  }

  function totalAmount(startTime, endTime, hourPrice, servicePrice) {
    const timeDifference = endTime - startTime;
    const totalMinutes = Math.ceil(timeDifference / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (minutes > 10) {
      return (hours + 0.5) * hourPrice + servicePrice;
    } else if (minutes > 30) {
      return (hours + 1) * hourPrice + servicePrice;
    } else {
      return hours * hourPrice + servicePrice;
    }
  }

  const handlePhoneCall = () => {
    const phoneNumber = bookingData?.staff?.phoneNumber;
    //console.log("Phone Number:", phoneNumber);
    Linking.openURL(`tel:${phoneNumber}`);
  };

  const statusStyles = {
    CREATED: { color: '#00BFFF', backgroundColor: '#00BFFF66', label: 'Created' },
    APPROVED: {
      color: '#654321',
      backgroundColor: '#65432166',
      label: 'approved',
    },
    ACCEPTED: {
      color: '#D2B55B',
      backgroundColor: '#D2B55B66',
      label: 'accepted',
    },
    ONGOING: {
      color: '#B7410E',
      backgroundColor: '#B7410E66',
      label: 'on-going',
    },
    ASSIGNED: {
      color: '#769DB9',
      backgroundColor: '#769DB966',
      label: 'assigned',
    },
    COMPLETED: {
      color: '#006A4E',
      backgroundColor: '#006A4E66',
      label: 'completed',
    },
    ENDED: { color: '#000080', backgroundColor: '#00008066', label: 'Ended' },
    REJECTED: {
      color: '#4D4B46',
      backgroundColor: '#4D4B4666',
      label: 'rejected',
    },
    CANCELLED: {
      color: '#C62828',
      backgroundColor: '#FFEBEE',
      label: 'cancelled'
    }
  };
  function allSmallLetters(input) {
    if (!input || typeof input !== 'string') {
      return input;
    }
    const lowerCase = input.toLowerCase();
    return lowerCase;
  }

  function allCapital(input) {
    if (!input || typeof input !== 'string') {
      return input;
    }
    const upperCase = input.toUpperCase();
    return upperCase;
  }
  useEffect(() => {
    if (bookingData?.serviceStartTime && bookingStatus === 'ONGOING') {
      const interval = setInterval(() => {
        const currentTime = Date.now();
        const elapsedTime = currentTime - bookingData.serviceStartTime * 1000;
        const totalSeconds = Math.floor(elapsedTime / 1000);
        const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
        const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(
          2,
          '0',
        );
        const seconds = String(totalSeconds % 60).padStart(2, '0');
        setTimer(`${hours}:${minutes}:${seconds}`);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setTimer('');
    }
  }, [bookingData, bookingStatus]);

  function calculatedPrice(firstvalue, secondvalue) {
    const price = firstvalue * secondvalue;
    return price;
  }

  useEffect(() => {
    getBookingDetail();
  }, [getBookingDetail, rerender]);

  useEffect(() => {
    AsyncStorage.getItem('userId').then(setLoggedInUserId);
  }, []);

  if (bookingData) {
    return (
      <View style={styles.screenWrapper}>
        <KidInfoModal kid={kidData} visible={visible} setVisible={setVisible} />
        <TopGreenHeader Heading="Details" isBackIcon={true} navigation={navigation} />
        <ScrollView style={styles.container}>
          {/* <ToastContainer /> */}
          <View style={styles.container}>


            <View style={styles.jobOverview}>
              <View style={styles.titleCard}>
                <Text style={styles.bookingTitle}>
                  {typeDescriptionMap[bookingData.type] ||
                    eventTypeLabel(bookingData.type) ||
                    bookingData.type}
                </Text>
                {bookingData.type == 'HOMECARE' ||
                  bookingData.type == 'LEARNCARE' ||
                  bookingData.type == 'SOCIALHUB' ||
                  bookingData.type == 'BREAKFASTCLUB' ? (
                  <>
                    <Text style={styles.bookingDate}>
                      {getTime()} ,{EpochTime(bookingData.startTime)} - {EpochTime(bookingData.endTime)}
                    </Text>
                  </>
                ) : null}

                {bookingData.type == 'TRANSPORT' ||
                  bookingData.type == 'BIRTHDAY' ||
                  bookingData.type == 'VENREN' ||
                  isEventType(bookingData.type) ? (
                  <>
                    <Text style={styles.bookingDate}>
                      {getTime()} {EpochTime(bookingData.startTime)}
                    </Text>
                  </>
                ) : null}
                <View
                  style={[
                    styles.statusBadge,
                    {
                      backgroundColor:
                        statusStyles[bookingData.status].backgroundColor,
                    },
                  ]}>
                  <Text
                    style={[
                      styles.patchText,
                      { color: statusStyles[bookingData.status].color },
                    ]}>
                    {allSmallLetters(bookingData.status)}
                  </Text>
                </View>
              </View>
            </View>

            <View style={[styles.childDetails, { marginBottom: 10 }]}>
              {bookingData?.kids?.map(kid => {
                return (
                  <TouchableOpacity
                    key={kid.kidId}
                    onPress={() => {
                      setKidData(kid);
                      setVisible(true);
                    }}>
                    <View style={[styles.childPicName]}>
                      <Image
                        style={styles.childPic}
                        source={{ uri: kid.profilePic }}
                      />
                      <View style={[styles.childNameAgeGender]}>
                        <Text style={styles.childName}>{kid.name}</Text>
                        <View style={styles.childAgeGender}>
                          <Text style={styles.childAge}>{kid.age}-years,</Text>
                          <Text style={styles.childAge}> {kid.gender === 'BOY' ? 'Boy' : kid.gender === 'GIRL' ? 'Girl' : kid.gender}</Text>
                        </View>
                      </View>
                      <Image
                        source={require('./assets/next1.png')}
                        style={{
                          position: 'absolute',
                          height: 25,
                          width: 12,
                          right: 10,
                        }}
                      />
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>

            <View style={{ paddingHorizontal: 16 }}>
              <Text style={styles.label}>
                Booking Id:{' '}
                <Text style={styles.bookingIdLabel}>{bookingData.bookingId}</Text>
              </Text>
              <Text style={styles.label}>
                Created At:{' '}
                <Text style={[styles.bookingLabel]}>
                  {formatEpochToLocalTime(bookingData.createdAt)}
                </Text>
              </Text>

              {(bookingData.type === 'HOMECARE' ||
                bookingData.type === 'TRANSPORT' ||
                isEventType(bookingData.type)) &&
                bookingData?.addresses?.length > 0 && (
                  <>
                    <Text
                      style={styles.label}>
                      Addresses:
                    </Text>
                    {bookingData.addresses.map((address, index) => (
                      <Text
                        key={index}
                        style={{
                          lineHeight: 22,
                          marginTop: 12,
                          fontSize: 18,
                          color: '#000',
                          fontFamily:
                            Platform.OS === 'android'
                              ? 'FacebookSansRegular'
                              : 'Facebook Sans',
                          fontStyle: 'normal',
                        }}>
                        {allCapital(address.tag)} - {address.houseno ? (address.houseno + ',') : ''}{' '}
                        {address.area}, {address.city}, {address.zipcode}.
                      </Text>
                    ))}
                  </>
                )}

              {bookingData?.parent?.discription && bookingData?.parent?.discription != "" &&
                (<Text style={styles.label}>
                  Instructions:{' '}
                  <Text style={[styles.bookingLabel]}>{bookingData?.parent?.discription}</Text>
                </Text>)
              }
            </View>

            {(bookingStatus == 'CREATED' ||
              bookingStatus == 'ONGOING' ||
              bookingStatus == 'ASSIGNED' ||
              bookingStatus == 'ENDED' ||
              bookingStatus == 'ACCEPTED' ||
              bookingStatus == 'COMPLETED' ||
              bookingStatus == 'APPROVED') &&
              bookingData.staff.staffId != null ? (
              <>
                <View style={styles.careTakerContainer}>
                  <Text style={styles.careTakerLabel}>
                    Care Taker :
                    <Text
                      style={{
                        fontSize: 18,
                        color: '#000',
                        fontFamily:
                          Platform.OS === 'android'
                            ? 'FacebookSansRegular'
                            : 'Facebook Sans',
                        fontStyle: 'normal',
                      }}>
                      {' '}
                      {bookingData.staff.name}
                    </Text>
                  </Text>
                  <TouchableOpacity
                    style={styles.phoneButton}
                    onPress={handlePhoneCall}>
                    <Image
                      source={require('./assets/phonecall1.png')}
                      style={styles.phoneIcon}
                    />
                  </TouchableOpacity>
                </View>
              </>
            ) : null}


            {bookingData?.bookingItems?.map(item => {
              return (
                <View
                  style={{
                    flexDirection: 'row',
                    marginLeft: 18,
                    marginTop: 8,
                  }}>
                  <Text
                    style={{
                      color: '#8E8E93',
                      fontSize: 18,
                      fontFamily:
                        Platform.OS === 'android'
                          ? 'FacebookSansRegular'
                          : 'Facebook Sans',
                      fontStyle: 'normal',
                    }}>
                    {item.itemTitle}{' '}
                  </Text>
                  <Text
                    style={{
                      fontSize: 18,
                      fontFamily:
                        Platform.OS === 'android'
                          ? 'FacebookSansRegular'
                          : 'Facebook Sans',
                      fontStyle: 'normal',
                    }}>
                    {item.itemQuantity} X {'$'}
                    {item.itemPrice} = {'$'}
                    {calculatedPrice(item.itemQuantity, item.itemPrice)}
                  </Text>
                </View>
              );
            })}

            {bookingStatus == 'ONGOING' ? (
              <>
                <View
                  style={{
                    flex: 1,
                    alignItems: 'center',
                  }}>
                  <Text
                    style={{
                      fontSize: 16,
                      color: '#8E8E93',
                      fontFamily:
                        Platform.OS === 'android'
                          ? 'FacebookSansRegular'
                          : 'Facebook Sans',
                      fontStyle: 'normal',
                    }}>
                    {' '}
                    Job started at (started at{' '}
                    {EpochTime(bookingData.serviceStartTime)})
                  </Text>
                  <Text
                    style={{
                      fontSize: 28,
                      color: '#000',
                      fontFamily:
                        Platform.OS === 'android'
                          ? 'FacebookSansRegular'
                          : 'Facebook Sans',
                      fontStyle: 'normal',
                    }}>
                    {timer}
                  </Text>
                </View>
              </>
            ) : null}

            {(bookingStatus == 'ONGOING') && (
              <TouchableOpacity style={styles.endButton} onPress={() => setEndJobModalVisible(true)}>
                <Text style={styles.endButtonText}>End Job</Text>
              </TouchableOpacity>
            )}

            {(bookingStatus == 'CREATED' || bookingStatus == 'ACCEPTED' || bookingStatus == 'ASSIGNED') && (
              <TouchableOpacity style={styles.endButton} onPress={() => setCancelModalVisible(true)}>
                <Text style={styles.endButtonText}>Cancel Booking</Text>
              </TouchableOpacity>
            )}

            {bookingData?.postServiceComment && bookingData?.postServiceComment != "" &&
              (<Text style={[styles.label, { marginLeft: 16 }]}>
                Post-Care Summary:{' '}
                <Text style={[styles.bookingLabel]}>{bookingData?.postServiceComment}</Text>
              </Text>)
            }



            {bookingStatus == 'APPROVED' &&
              (bookingData.type !== 'BIRTHDAY' ||
                bookingData.type !== 'VENREN') ? (
              <>
                <View style={{ marginLeft: 18 }}>
                  <Text
                    style={{
                      marginTop: 8,
                      fontSize: 18,
                      color: '#8E8E93',
                      fontFamily:
                        Platform.OS === 'android'
                          ? 'FacebookSansRegular'
                          : 'Facebook Sans',
                      fontStyle: 'normal',
                    }}>
                    Service Time:{' '}
                    <Text
                      style={{
                        marginTop: 8,
                        fontSize: 18,
                        color: '#000',
                        fontFamily:
                          Platform.OS === 'android'
                            ? 'FacebookSansRegular'
                            : 'Facebook Sans',
                        fontStyle: 'normal',
                      }}>
                      {EpochTime(bookingData.serviceStartTime)}
                      {' - '}
                      {EpochTime(bookingData.serviceEndTime)}
                    </Text>
                  </Text>
                  <Text
                    style={{
                      marginTop: 8,
                      fontSize: 18,
                      color: '#8E8E93',
                      fontFamily:
                        Platform.OS === 'android'
                          ? 'FacebookSansRegular'
                          : 'Facebook Sans',
                      fontStyle: 'normal',
                    }}>
                    Total Time & Service Charges:{' '}
                    <Text
                      style={{
                        marginTop: 8,
                        fontSize: 18,
                        color: '#000',
                        fontFamily:
                          Platform.OS === 'android'
                            ? 'FacebookSansRegular'
                            : 'Facebook Sans',
                        fontStyle: 'normal',
                      }}>
                      {calculateServiceTime(
                        bookingData.serviceStartTime,
                        bookingData.serviceEndTime,
                      )}
                      {' x $'}
                      {bookingData.perHourCharged}/hr
                      {' = $'}
                      {bookingData.serviceCharges}
                      {/* {' = $'}{totalAmount(bookingData.serviceStartTime, bookingData.serviceEndTime, bookingData.perHourCharged, bookingData.serviceCharges)} */}
                    </Text>
                  </Text>

                  <Text
                    style={{
                      marginTop: 8,
                      fontSize: 18,
                      color: '#8E8E93',
                      fontFamily:
                        Platform.OS === 'android'
                          ? 'FacebookSansRegular'
                          : 'Facebook Sans',
                      fontStyle: 'normal',
                    }}>
                    Amount :{' '}
                    <Text
                      style={{
                        marginTop: 8,
                        fontSize: 18,
                        color: '#000',
                        fontFamily:
                          Platform.OS === 'android'
                            ? 'FacebookSansRegular'
                            : 'Facebook Sans',
                        fontStyle: 'normal',
                      }}>
                      {' '}
                      {'$'}
                      {bookingData.amount}
                    </Text>
                  </Text>
                  <Text
                    style={{
                      marginTop: 8,
                      fontSize: 18,
                      color: '#8E8E93',
                      fontFamily:
                        Platform.OS === 'android'
                          ? 'FacebookSansRegular'
                          : 'Facebook Sans',
                      fontStyle: 'normal',
                    }}>
                    Status:{' '}
                    <Text
                      style={{
                        marginTop: 8,
                        fontSize: 18,
                        color: '#000',
                        fontFamily:
                          Platform.OS === 'android'
                            ? 'FacebookSansRegular'
                            : 'Facebook Sans',
                        fontStyle: 'normal',
                      }}>
                      {bookingData.paymentStatus}
                    </Text>
                    {bookingData.paymentStatus === 'PARTIALPAID' ? (
                      <>
                        <Text
                          style={{
                            marginTop: 8,
                            fontSize: 18,
                            color: '#000',
                            fontFamily:
                              Platform.OS === 'android'
                                ? 'FacebookSansRegular'
                                : 'Facebook Sans',
                            fontStyle: 'normal',
                          }}>
                          {' '}
                          {'('}
                          {'Paid: '}
                          {'$'}
                          {bookingData.invoiceDetails.paidAmount}
                          {', '}
                          {'Balance: '}
                          {'$'}
                          {bookingData.invoiceDetails.balance}
                          {')'}
                        </Text>
                      </>
                    ) : null}
                  </Text>
                </View>
                <TouchableOpacity
                  onPress={() =>
                    navigation.navigate('Profilebookings', {
                      invoiceId: bookingData.invoiceDetails.invoiceId,
                    })
                  }
                  style={styles.updateButton}>
                  <Text style={styles.updateButtonText}>View Invoice</Text>
                </TouchableOpacity>

                {bookingData.paymentStatus !== 'PAID' && (
                  <TouchableOpacity
                    onPress={() => {
                      // const invoiceLink =
                      //   bookingData?.invoiceDetails?.qbJSON?.Invoice?.InvoiceLink;
                      const invoiceLink = `https://freedommerchants.com/danileeshhmulti.php?inum=${bookingData.invoiceDetails.invoiceId}`;
                      if (invoiceLink) {
                        Linking.openURL(invoiceLink);
                      } else {
                        console.warn('Invoice link is not available');
                      }
                    }}
                    style={[styles.updateButton, { marginBottom: 20 }]}>
                    <Text style={styles.updateButtonText}>
                      Pay {'$'}
                      {bookingData.amount}
                    </Text>
                  </TouchableOpacity>
                )}
              </>
            ) : null}

            {bookingStatus == 'ENDED' ? (
              <>
                <View style={{ marginLeft: 18 }}>
                  <Text
                    style={{
                      marginTop: 8,
                      fontSize: 18,
                      color: '#8E8E93',
                      fontFamily:
                        Platform.OS === 'android'
                          ? 'FacebookSansRegular'
                          : 'Facebook Sans',
                      fontStyle: 'normal',
                    }}>
                    Service Time:{' '}
                    <Text
                      style={{
                        marginTop: 8,
                        fontSize: 18,
                        color: '#000',
                        fontFamily:
                          Platform.OS === 'android'
                            ? 'FacebookSansRegular'
                            : 'Facebook Sans',
                        fontStyle: 'normal',
                      }}>
                      {EpochTime(bookingData.serviceStartTime)}
                      {' - '}
                      {EpochTime(bookingData.serviceEndTime)}
                    </Text>
                  </Text>
                  <Text
                    style={{
                      marginTop: 8,
                      fontSize: 18,
                      color: '#8E8E93',
                      fontFamily:
                        Platform.OS === 'android'
                          ? 'FacebookSansRegular'
                          : 'Facebook Sans',
                      fontStyle: 'normal',
                    }}>
                    Total Time & Service Charges:{' '}
                    <Text
                      style={{
                        marginTop: 8,
                        fontSize: 18,
                        color: '#000',
                        fontFamily:
                          Platform.OS === 'android'
                            ? 'FacebookSansRegular'
                            : 'Facebook Sans',
                        fontStyle: 'normal',
                      }}>
                      {calculateServiceTime(
                        bookingData.serviceStartTime,
                        bookingData.serviceEndTime,
                      )}
                      {' x $'}
                      {bookingData.perHourCharged}/hr
                      {' = $'}
                      {bookingData.serviceCharges}
                      {/* {' = $'}{totalAmount(bookingData.serviceStartTime, bookingData.serviceEndTime, bookingData.perHourCharged, bookingData.serviceCharges)} */}
                    </Text>
                  </Text>

                  <Text
                    style={{
                      marginTop: 8,
                      fontSize: 18,
                      color: '#8E8E93',
                      fontFamily:
                        Platform.OS === 'android'
                          ? 'FacebookSansRegular'
                          : 'Facebook Sans',
                      fontStyle: 'normal',
                    }}>
                    Amount :{' '}
                    <Text
                      style={{
                        marginTop: 8,
                        fontSize: 18,
                        color: '#000',
                        fontFamily:
                          Platform.OS === 'android'
                            ? 'FacebookSansRegular'
                            : 'Facebook Sans',
                        fontStyle: 'normal',
                      }}>
                      {' '}
                      {'$'}
                      {bookingData.amount}
                    </Text>
                  </Text>
                  <Text
                    style={{
                      marginTop: 8,
                      fontSize: 18,
                      color: '#8E8E93',
                      fontFamily:
                        Platform.OS === 'android'
                          ? 'FacebookSansRegular'
                          : 'Facebook Sans',
                      fontStyle: 'normal',
                    }}>
                    Status:{' '}
                    <Text
                      style={{
                        marginTop: 8,
                        fontSize: 18,
                        color: '#000',
                        fontFamily:
                          Platform.OS === 'android'
                            ? 'FacebookSansRegular'
                            : 'Facebook Sans',
                        fontStyle: 'normal',
                      }}>
                      {bookingData.paymentStatus}
                    </Text>
                    {bookingData.paymentStatus === 'PARTIALPAID' ? (
                      <>
                        <Text
                          style={{
                            marginTop: 8,
                            fontSize: 18,
                            color: '#000',
                            fontFamily:
                              Platform.OS === 'android'
                                ? 'FacebookSansRegular'
                                : 'Facebook Sans',
                            fontStyle: 'normal',
                          }}>
                          {' '}
                          {'('}
                          {'Paid: '}
                          {'$'}
                          {bookingData.invoiceDetails.paidAmount}
                          {', '}
                          {'Balance: '}
                          {'$'}
                          {bookingData.invoiceDetails.balance}
                          {')'}
                        </Text>
                      </>
                    ) : null}
                  </Text>
                </View>

                <TouchableOpacity
                  style={[styles.updateButton, { marginBottom: 20 }]}
                  onPress={handleApprove}
                  disabled={loading}>
                  <Text style={[styles.updateButtonText]}>
                    {loading ? 'Approving...' : 'Approve'}
                  </Text>
                </TouchableOpacity>
              </>
            ) : null}

            {bookingStatus == 'COMPLETED' ? (
              <>
                <View style={{ marginLeft: 16 }}>
                  <Text
                    style={{
                      marginTop: 8,
                      fontSize: 18,
                      color: '#8E8E93',
                      fontFamily:
                        Platform.OS === 'android'
                          ? 'FacebookSansRegular'
                          : 'Facebook Sans',
                      fontStyle: 'normal',
                    }}>
                    Service Time:{' '}
                    <Text
                      style={{
                        marginTop: 8,
                        fontSize: 18,
                        color: '#000',
                        fontFamily:
                          Platform.OS === 'android'
                            ? 'FacebookSansRegular'
                            : 'Facebook Sans',
                        fontStyle: 'normal',
                      }}>
                      {EpochTime(bookingData.serviceStartTime)}
                      {' - '}
                      {EpochTime(bookingData.serviceEndTime)}
                    </Text>
                  </Text>
                  <Text
                    style={{
                      marginTop: 8,
                      fontSize: 18,
                      color: '#8E8E93',
                      fontFamily:
                        Platform.OS === 'android'
                          ? 'FacebookSansRegular'
                          : 'Facebook Sans',
                      fontStyle: 'normal',
                    }}>
                    Total Time & Service Charges:{' '}
                    <Text
                      style={{
                        marginTop: 8,
                        fontSize: 18,
                        color: '#000',
                        fontFamily:
                          Platform.OS === 'android'
                            ? 'FacebookSansRegular'
                            : 'Facebook Sans',
                        fontStyle: 'normal',
                      }}>
                      {calculateServiceTime(
                        bookingData.serviceStartTime,
                        bookingData.serviceEndTime,
                      )}
                      {' x $'}
                      {bookingData.perHourCharged}/hr
                      {' = $'}
                      {bookingData.serviceCharges}
                      {/* {' = $'}{totalAmount(bookingData.serviceStartTime, bookingData.serviceEndTime, bookingData.perHourCharged, bookingData.serviceCharges)} */}
                    </Text>
                  </Text>

                  <Text
                    style={{
                      marginTop: 8,
                      fontSize: 18,
                      color: '#8E8E93',
                      fontFamily:
                        Platform.OS === 'android'
                          ? 'FacebookSansRegular'
                          : 'Facebook Sans',
                      fontStyle: 'normal',
                    }}>
                    Amount :{' '}
                    <Text
                      style={{
                        marginTop: 8,
                        fontSize: 18,
                        color: '#000',
                        fontFamily:
                          Platform.OS === 'android'
                            ? 'FacebookSansRegular'
                            : 'Facebook Sans',
                        fontStyle: 'normal',
                      }}>
                      {' '}
                      {bookingData.amount}
                    </Text>
                  </Text>
                  <Text
                    style={{
                      marginTop: 8,
                      fontSize: 18,
                      color: '#8E8E93',
                      fontFamily:
                        Platform.OS === 'android'
                          ? 'FacebookSansRegular'
                          : 'Facebook Sans',
                      fontStyle: 'normal',
                    }}>
                    Status:{' '}
                    <Text
                      style={{
                        marginTop: 8,
                        fontSize: 18,
                        color: '#000',
                        fontFamily:
                          Platform.OS === 'android'
                            ? 'FacebookSansRegular'
                            : 'Facebook Sans',
                        fontStyle: 'normal',
                      }}>
                      {bookingData.paymentStatus}
                    </Text>
                    {bookingData.paymentStatus === 'PARTIALPAID' ? (
                      <>
                        <Text
                          style={{
                            marginTop: 8,
                            fontSize: 18,
                            color: '#000',
                            fontFamily:
                              Platform.OS === 'android'
                                ? 'FacebookSansRegular'
                                : 'Facebook Sans',
                            fontStyle: 'normal',
                          }}>
                          {' '}
                          {'('}
                          {'Paid: '}
                          {'$'}
                          {bookingData.invoiceDetails.paidAmount}
                          {', '}
                          {'Balance: '}
                          {'$'}
                          {bookingData.invoiceDetails.balance}
                          {')'}
                        </Text>
                      </>
                    ) : null}
                  </Text>
                </View>
                <TouchableOpacity
                  onPress={() =>
                    navigation.navigate('Profilebookings', {
                      invoiceId: bookingData.invoiceDetails.invoiceId,
                    })
                  }
                  style={styles.updateButton}>
                  <Text style={styles.updateButtonText}>View Invoice</Text>
                </TouchableOpacity>
              </>
            ) : null}

            {bookingStatus == 'REJECTED' ? (
              <>
                <Text
                  style={{
                    marginLeft: 14,
                    marginTop: 8,
                    fontSize: 18,
                    color: '#8E8E93',
                    fontFamily:
                      Platform.OS === 'android'
                        ? 'FacebookSansRegular'
                        : 'Facebook Sans',
                    fontStyle: 'normal',
                  }}>
                  Reason :
                  <Text
                    style={{
                      fontSize: 18,
                      color: '#000',
                      fontFamily:
                        Platform.OS === 'android'
                          ? 'FacebookSansRegular'
                          : 'Facebook Sans',
                      fontStyle: 'normal',
                    }}>
                    {bookingData.bookingRejectionReason}
                  </Text>
                </Text>
              </>
            ) : null}
          </View>
        </ScrollView>

        <Modal
          animationType="slide"
          transparent={true}
          visible={cancelModalVisible}
          onRequestClose={() => setCancelModalVisible(false)}
        >
          <View style={styles.modalContainer}>
            <View style={styles.modalContent}>
              <Text style={styles.modalText}>Cancel Booking</Text>
              <TextInput
                style={[styles.input, { width: '100%', height: 120, textAlignVertical: 'top', padding: 10, fontSize: 16 }]}
                placeholder="Reason for cancellation..."
                placeholderTextColor="#505766"
                value={cancelReason}
                onChangeText={setCancelReason}
                multiline={true}
                numberOfLines={4}
              />
              {isWithin24Hrs() && (
                <Text style={{ color: 'red', marginTop: 10, fontSize: 14, fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans' }}>
                  Note: Since this booking is being cancelled in less than 24 hrs window, you will be charged ${CANCELLATION_FEE} as cancellation fee.
                </Text>
              )}
              <View style={styles.modalButtons}>
                <TouchableOpacity
                  style={styles.button}
                  onPress={handleCancelBooking}
                  disabled={loading}
                >
                  <Text style={styles.buttonText}>{loading ? "Cancelling..." : "Confirm"}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.button, { backgroundColor: '#ccc' }]}
                  onPress={() => setCancelModalVisible(false)}
                >
                  <Text style={styles.buttonText}>Close</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>

        <Modal
          animationType="slide"
          transparent={true}
          visible={endJobModalVisible}
          onRequestClose={() => setEndJobModalVisible(false)}
        >
          <View style={styles.modalContainer}>
            <View style={styles.modalContent}>
              <Text style={styles.modalText}>Do you really want to end the job?</Text>
              <View style={styles.modalButtons}>
                <TouchableOpacity
                  style={styles.button}
                  onPress={handleEndJob}
                  disabled={loading}
                >
                  <Text style={styles.buttonText}>{loading ? "Ending..." : "Confirm"}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.button, { backgroundColor: '#ccc' }]}
                  onPress={() => setEndJobModalVisible(false)}
                >
                  <Text style={styles.buttonText}>Cancel</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>

        {canShowChatButton() && (
          <TouchableOpacity style={styles.chatButton} onPress={handleOpenChat}>
            <Text style={styles.chatButtonText}>💬</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  } else {
    return (
      <View style={styles.container}>
        <ActivityIndicator
          size="large"
          color="#A6CE39"
          style={{
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        />
      </View>
    );
  }
}

const styles = StyleSheet.create({
  screenWrapper: {
    flex: 1,
    position: 'relative',
  },
  chatButton: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#A6CE39',
    alignItems: 'center',
    justifyContent: 'center',
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  chatButtonText: {
    fontSize: 26,
  },
  container: {
    flex: 1,
    backgroundColor: '#fff',
    //overflowY:'auto'
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: '#A6CE39',
    padding: 15,
  },
  headerText: {
    color: '#fff',
    fontSize: 30,
    alignSelf: 'center',
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
    fontWeight: 'bold',
  },
  backButtonLogo: {
    width: 15,
    height: 28,
    marginTop: 4,
  },

  notificationButtonLogo: {
    width: 30,
    height: 30,
    marginTop: 4,
  },
  jobOverview: {
    width: '100%',
    flexDirection: 'row',
  },
  bookingTitle: {
    fontSize: 18,
    color: '#1A1A1A',
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
  },
  bookingDate: {
    fontSize: 14,
    marginTop: 2,
    color: '#666',
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
  },
  titleCard: {
    marginTop: 20,
    marginLeft: 16,
    width: '50%',
  },
  statusBadge: {
    width: 100,
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 50,
    marginTop: 4,
  },
  patchText: {
    textAlign: 'center',
    fontSize: 14,
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
    fontWeight: 'bold',
  },
  careTakerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
    padding: 16,
    backgroundColor: '#ffffff',
    marginTop: 16,
    marginHorizontal: 10,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  careTakerLabel: {
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
    color: '#8E8E93',
    fontSize: 18,
  },
  phoneIcon: {
    width: 30,
    height: 30,
  },
  endButton: {
    width: '45%',
    height: 45,
    alignSelf: 'center',
    backgroundColor: '#A6CE39',
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
  },
  endButtonText: {
    fontFamily: Platform.OS === 'android' ? 'FacebookSansBold' : 'Facebook Sans',
    color: '#fff',
    fontSize: 18,
  },
  timer: {
    fontSize: 18,
    color: '#1A1A1A',
    marginTop: 20,
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
  },
  jobDetails: {
    width: '100%',
    marginTop: 22,
    marginLeft: 16,
  },
  label: {
    fontSize: 18,
    marginTop: 8,
    color: '#8E8E93',
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
  },
  bookingIdLabel: {
    color: '#EE3897',
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
    fontWeight: 'bold',
  },
  bookingLabel: {
    fontSize: 18,
    color: '#1A1A1A',
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
  },
  childDetails: {
    marginHorizontal: 16,
    marginTop: 22,
    backgroundColor: '#fff',
    borderRadius: 10,
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  childPicName: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  childPic: {
    borderRadius: 50,
    marginLeft: 16,
    marginTop: 6,
    marginBottom: 6,
    height: 50,
    width: 50,
  },
  childNameAgeGender: {
    marginLeft: 25,
  },
  childName: {
    fontSize: 18,
    color: '#1A1A1A',
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
    fontWeight: 'bold',
  },
  childAgeGender: {
    flexDirection: 'row',
    fontSize: 12,
  },
  childAge: {
    color: '#8E8E93',
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
  },
  updateButton: {
    backgroundColor: '#A6CE39',
    padding: 9,
    marginTop: 10,
    marginBottom: 20,
    alignSelf: 'center',
    width: 162,
  },
  updateButtonText: {
    color: '#fff',
    textAlign: 'center',
    fontSize: 18,
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
    fontWeight: 'bold',
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  modalContent: {
    width: '80%',
    padding: 30,
    backgroundColor: '#fff',
    borderRadius: 10,
    alignItems: 'center',
  },
  modalText: {
    fontSize: 18,
    marginBottom: 20,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansBold' : 'Facebook Sans',
    textAlign: 'center',
    color: '#000'
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    marginTop: 15,
  },
  button: {
    flex: 1,
    padding: 12,
    marginHorizontal: 10,
    backgroundColor: '#A6CE39',
    alignItems: 'center',
    borderRadius: 8,
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontFamily: Platform.OS === 'android' ? 'FacebookSansBold' : 'Facebook Sans',
    textAlign: 'center',
  },
  input: {
    borderColor: '#ddd',
    borderWidth: 1,
    color: '#000',
    borderRadius: 10,
    backgroundColor: '#f9f9f9',
    fontFamily:
      Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    fontStyle: 'normal',
  },
});
